import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { LeverageSlider } from "@/features/leverage/leverage-slider";
import { useTradingSettings } from "./trading-settings-context";
import { useProduct } from "@/features/product/product-context";
import { formatCurrency, cn } from "@/lib/utils";
import { useUnifiedAuth } from "@/backend/use-unified-auth";
import { toast } from "sonner";
import { 
  fetchLeverageConfig, 
  getMaxPositionFromConfig, 
  type LeverageConfig 
} from "./types";
import { useAccountStore } from "@/backend/store";
import { PositionsApi } from "@/backend/api-client";
import { EditLeverageModal } from "@/features/positions/edit-leverage-modal";

// Base leverage options - will be filtered and extended based on max leverage
const BASE_LEVERAGE_OPTIONS = [1, 2, 5, 10, 25, 50, 100];

type LeverageDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const LeverageDialog = ({
  open,
  onOpenChange,
}: LeverageDialogProps) => {
  const { getLeverage, setLeverage } = useTradingSettings();
  const { product } = useProduct();
  const { userId, isAuthenticated } = useUnifiedAuth();
  
  const market = product?.id || "SOL/USD";
  const currentLeverage = getLeverage(market);
  
  const [selectedLeverage, setSelectedLeverage] = useState(currentLeverage);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [leverageConfig, setLeverageConfig] = useState<LeverageConfig | null>(null);

  // Get effective user ID (works for both wallet and email users)
  const effectiveUserId = userId;
  const isEffectivelyConnected = isAuthenticated;

  // Fetch leverage config when dialog opens or market changes
  useEffect(() => {
    if (open && market) {
      fetchLeverageConfig(market).then(setLeverageConfig);
    }
  }, [open, market]);

  // Get max leverage from API config, or fall back to system absolute maximum
  const maxLeverage = leverageConfig?.max_leverage || 100; // 100 = ABSOLUTE_MAX_LEVERAGE

  // Calculate max position based on leverage tier (using API config if available)
  const maxPosition = useMemo(() => {
    return getMaxPositionFromConfig(selectedLeverage, leverageConfig);
  }, [selectedLeverage, leverageConfig]);

  // Generate dynamic leverage presets based on max leverage
  const leveragePresets = useMemo(() => {
    // Filter to only show presets <= maxLeverage
    const filtered = BASE_LEVERAGE_OPTIONS.filter(lev => lev <= maxLeverage);
    // Always include maxLeverage if not already present
    if (!filtered.includes(maxLeverage)) {
      filtered.push(maxLeverage);
      filtered.sort((a, b) => a - b);
    }
    return filtered;
  }, [maxLeverage]);

  // Update selected leverage when dialog opens or current leverage changes
  useEffect(() => {
    if (open) {
      setSelectedLeverage(currentLeverage);
      setError(null);
    }
  }, [open, currentLeverage]);

  // Clamp selected leverage if max leverage changes and current selection exceeds it
  useEffect(() => {
    if (selectedLeverage > maxLeverage) {
      setSelectedLeverage(maxLeverage);
    }
  }, [maxLeverage, selectedLeverage]);

  // Check if there's an existing position for this market
  const positionsMap = useAccountStore((state) => state.positions);
  const existingPosition = positionsMap.get(market);
  const hasExistingPosition = existingPosition && existingPosition.size > 0;

  // If user has an active position, show the detailed modal with margin info instead
  // This follows industry standard (Binance, Bybit) - users need to see margin impact
  if (hasExistingPosition && existingPosition) {
    return (
      <EditLeverageModal
        open={open}
        onOpenChange={onOpenChange}
        position={existingPosition}
      />
    );
  }

  const handleConfirm = async () => {
    if (!isEffectivelyConnected || !effectiveUserId) {
      toast.error("Please connect your wallet first");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Single API call handles everything: position updates + saving settings for future orders
      // Works for all cases:
      // - Cross margin with position: Updates position leverage + saves setting
      // - Isolated margin with position: Returns error (must close position first)
      // - No position (any mode): Saves leverage setting for future orders
      await PositionsApi.updateLeverage(market, selectedLeverage);

      // Update local state
      setLeverage(market, selectedLeverage);
      
      // Immediately refetch position to update UI (don't wait for WebSocket)
      // This ensures the position table and order form button show the new leverage immediately
      if (hasExistingPosition) {
        try {
          const positions = await PositionsApi.listPositions();
          const updatedPosition = positions.find((p) => p.market === market);
          if (updatedPosition) {
            // Update the position in the store using the HTTP sync method
            useAccountStore.getState().updatePositionFromHttp(updatedPosition);
          }
        } catch (err) {
          console.warn("[LeverageDialog] Failed to refetch position after leverage update:", err);
          // Don't block the success flow if refetch fails
        }
      }
      
      // Show appropriate success message based on margin mode and position status
      let message: string;
      if (hasExistingPosition) {
        message = `Leverage updated to ${selectedLeverage}x for ${market} (position and new orders)`;
      } else {
        message = `Leverage set to ${selectedLeverage}x for ${market}`;
      }
      
      toast.success(message);
      onOpenChange(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to update leverage";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setSelectedLeverage(currentLeverage);
    setError(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Change Leverage
            <span className="text-sm font-normal px-2 py-0.5 rounded bg-muted text-muted-foreground">
              {market}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Current Leverage - separate row */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Current Leverage</span>
            <span className="font-medium">{currentLeverage}x</span>
          </div>

          {/* Leverage Selector with Slider and Presets */}
          <div className="space-y-2">
            <LeverageSlider
              value={selectedLeverage}
              onChange={setSelectedLeverage}
              max={maxLeverage}
            />
            
            {/* Preset buttons - styled to match Change Leverage modal */}
            <div className="flex gap-2 mt-3">
              {leveragePresets.map((lev) => (
                <button
                  key={lev}
                  onClick={() => setSelectedLeverage(lev)}
                  className={cn(
                    "flex-1 py-1.5 text-xs font-medium rounded border transition-colors",
                    selectedLeverage === lev
                      ? "border-buy bg-buy/10 text-buy"
                      : "border-border bg-muted/30 text-muted-foreground hover:border-muted-foreground hover:text-foreground"
                  )}
                >
                  {lev}x
                </button>
              ))}
            </div>
          </div>

          {/* Maximum position info */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Maximum position at {selectedLeverage}x</span>
            <span className="font-medium">{formatCurrency(maxPosition, 0)} USD</span>
          </div>

          {error && (
            <div className="text-xs text-destructive bg-destructive/10 p-2 rounded">
              {error}
            </div>
          )}

          {/* Help text */}
          <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-md">
            Leverage affects margin requirements and liquidation distance.
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-2">
          <Button 
            variant="outline" 
            onClick={handleCancel} 
            className="flex-1 h-11" 
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirm} 
            className="flex-1 h-11 bg-buy hover:bg-buy/90 text-white" 
            disabled={isSubmitting}
          >
            {isSubmitting ? "Updating..." : "Confirm"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

